import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, LayerType } from '../types/layer';
import type { LayerGroup } from '../types/layer';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './QGISLayerPanel.css';
import LayerSettingsModal from './LayerSettingsModal';

// 드래그 가능한 레이어 아이템 컴포넌트
interface LayerItemProps {
  layer: Layer;
  onToggleVisibility: (id: string) => void;
  onOpacityChange: (id: string, opacity: number) => void;
  onRemoveLayer?: (id: string) => void;
  onToggleFileVisibility?: (layerId: string, fileIndex: number, visible: boolean) => void;

  onTifFileSelect?: (layerId: string, fileIndex: number) => void;
  onTifFileDoubleClick?: (layerId: string, fileIndex: number) => void;
  onLayerDoubleClick?: (layerId: string) => void; // 레이어명 더블클릭 핸들러 추가
  onEgisLayerClick?: () => void; // EGIS 레이어 클릭 핸들러 추가
  onOpenLayerSettings?: (layerId: string, layerName: string, files: any[]) => void; // 레이어 설정 모달 열기 핸들러 추가
  isDraggable?: boolean;
}

const LayerItem: React.FC<LayerItemProps> = ({ 
  layer, 
  onToggleVisibility, 
  onOpacityChange, 
  onRemoveLayer,
  onToggleFileVisibility,

  onTifFileSelect,
  onTifFileDoubleClick,
  onLayerDoubleClick,
  onEgisLayerClick,
  onOpenLayerSettings,
  isDraggable = false
}) => {
  const { t } = useTranslation();
  // 드래그 앤 드롭 기능 설정
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id, disabled: !isDraggable });

  // 개별 레이어 접기/펼치기 상태 (사용자 정의 레이어만) - 초기 접속 시 접힌 상태
  const [expanded, setExpanded] = useState(() => {
    if (layer.type !== LayerType.EGIS && layer.files && layer.files.length > 0) {
      const saved = localStorage.getItem(`layerItem_${layer.id}_expanded`);
      return saved !== null ? JSON.parse(saved) : false; // 초기값을 false로 변경
    }
    return false; // EGIS 레이어도 초기값을 false로 변경
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  // 레이어 타입별 아이콘 반환
  const getLayerIcon = (type: LayerType) => {
    switch (type) {
      case LayerType.EGIS:
        return '🗺️';
      case LayerType.USER_TIF:
        return '📊';
      case LayerType.USER_GEOJSON:
        return '📍';
      case LayerType.USER_SHAPEFILE:
        return '📁';
      case LayerType.USER_KML:
        return '🗺️';
      case LayerType.USER_CSV:
        return '📋';
      case LayerType.USER_LAYER:
        return '📄';
      default:
        return '📄';
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    console.log(`체크박스 변경: ${layer.name} (${layer.id}) - ${isChecked}`);
    
    // EGIS 레이어의 경우 visible 상태를 명시적으로 설정
    if (layer.type === LayerType.EGIS) {
      console.log(`EGIS 레이어 visible 상태 변경: ${isChecked}`);
    }
    
    onToggleVisibility(layer.id);
    
    // 사용자 정의 레이어이고 파일이 있는 경우, 모든 파일의 가시성도 함께 변경
    if (layer.type === LayerType.USER_TIF && layer.files && layer.files.length > 0 && onToggleFileVisibility) {
      layer.files.forEach((file: any, index: number) => {
        onToggleFileVisibility(layer.id, index, isChecked);
      });
      console.log(`${layer.name}의 모든 파일 가시성 변경: ${isChecked}`);
    }
  };

  // 체크박스 상태 결정 (EGIS와 사용자 정의 레이어 구분)
  const getCheckboxState = () => {
    if (layer.type === LayerType.USER_TIF && layer.files && layer.files.length > 0) {
      // 사용자 정의 레이어: 파일 중 하나라도 체크되어 있으면 레이어도 체크된 것으로 표시
      return layer.files.some(file => file.visible !== false);
    } else if (layer.type === LayerType.EGIS) {
      // EGIS 레이어: visible 상태를 명시적으로 boolean으로 변환
      return layer.visible === true;
    }
    // 기타 레이어: visible 상태 그대로 사용
    return layer.visible;
  };

  // 개별 레이어 접기/펼치기 토글 (사용자 정의 레이어만)
  const handleToggleExpanded = () => {
    if (layer.type !== LayerType.EGIS && layer.files && layer.files.length > 0) {
      const newExpanded = !expanded;
      setExpanded(newExpanded);
      localStorage.setItem(`layerItem_${layer.id}_expanded`, JSON.stringify(newExpanded));
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`qgis-layer-item ${getCheckboxState() ? 'visible' : 'hidden'} ${isDragging ? 'dragging' : ''}`}
    >
      <div className="layer-header">
        <div className="layer-info">
          {/* 사용자 정의 레이어에만 접기/펼치기 아이콘 표시 */}
          {layer.type !== LayerType.EGIS && layer.files && layer.files.length > 0 && (
            <span 
              className="expand-icon" 
              onClick={handleToggleExpanded}
              style={{ cursor: 'pointer', marginRight: '4px' }}
              title="접기/펼치기"
            >
              {expanded ? '−' : '+'}
            </span>
          )}
          <span className="layer-icon">{getLayerIcon(layer.type)}</span>
          <input
            type="checkbox"
            checked={getCheckboxState()}
            onChange={handleCheckboxChange}
            className="layer-checkbox"
          />
          <span 
            className="layer-name" 
            onClick={() => {
              // EGIS 레이어 클릭 시 한국으로 이동
              if (layer.type === LayerType.EGIS && onEgisLayerClick) {
                onEgisLayerClick();
              }
            }}
            onDoubleClick={(e) => {
              e.stopPropagation(); // 클릭 이벤트 전파 방지
              // 사용자 정의 레이어 더블클릭 시 레이어 전체 영역으로 이동
              if (layer.type === LayerType.USER_TIF && onLayerDoubleClick) {
                onLayerDoubleClick(layer.id);
                console.log(`레이어명 더블클릭됨: ${layer.name}, 레이어 전체 영역으로 이동`);
              }
            }}
            style={{ 
              cursor: layer.type === LayerType.USER_TIF ? 'pointer' : (layer.type === LayerType.EGIS ? 'pointer' : 'default'),
              textDecoration: layer.type === LayerType.USER_TIF ? 'underline' : (layer.type === LayerType.EGIS ? 'underline' : 'none')
            }}
            title={layer.type === LayerType.EGIS ? t('common.clickToMoveToKorea') as string : 
                   layer.type === LayerType.USER_TIF ? t('common.doubleClickToMoveToLayer') as string : ''}
          >
            {layer.name}
          </span>
          {isDraggable && (
            <div 
              className="drag-handle"
              {...attributes}
              {...listeners}
              title={t('common.dragToMove') as string}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 19V5h2v14H7zm4-14v14h2V5h-2zm4 0v14h2V5h-2z"/>
              </svg>
            </div>
          )}
          {/* 회전 설정 버튼 (사용자 정의 레이어에만 표시) */}
          {layer.type !== LayerType.EGIS && layer.files && layer.files.length > 0 && (
            <button
              className="rotation-settings-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenLayerSettings) {
                  onOpenLayerSettings(layer.id, layer.name, layer.files || []);
                }
              }}
              title={t('common.layerSettings') as string}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 0 0-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 0 0-2.282.819l-.922 1.597a1.875 1.875 0 0 0 .432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 0 0 0 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 0 0-.432 2.385l.922 1.597a1.875 1.875 0 0 0 2.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 0 0 2.28-.819l.923-1.597a1.875 1.875 0 0 0-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 0 0 0-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 0 0-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 0 0-.985-.57c-.183-.087-.277-.227-.297-.348L9.928 3.817c-.151-.904-.933-1.567-1.85-1.567h-1.844ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" clipRule="evenodd"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* 파일 목록 표시 (사용자 정의 레이어인 경우) */}
      {layer.files && layer.files.length > 0 && expanded && (
        <div className="layer-files">
          <div className="files-header">
            <span className="files-title">{t('layers.fileList')} ({layer.files.length})</span>
          </div>
          <div className="files-list">
            {layer.files.map((file: any, index: number) => (
              <div key={index} className="file-item">
                <input
                  type="checkbox"
                  checked={file.visible !== false} // 기본값은 true
                  onChange={(e) => {
                    if (onToggleFileVisibility) {
                      onToggleFileVisibility(layer.id, index, e.target.checked);
                    }
                    console.log(`파일 ${file.name} 가시성 변경:`, e.target.checked);
                  }}
                  className="file-checkbox"
                />
                <span 
                  className="file-name"
                  onDoubleClick={() => {
                    if (onTifFileDoubleClick) {
                      onTifFileDoubleClick(layer.id, index);
                      console.log(`파일 ${file.name} 더블클릭됨, 지도 이동 요청`);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  title={t('common.doubleClickToMoveToFile') as string}
                >
                  {file.name}
                </span>
                {file.size && (
                  <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                )}
                
                {/* 회전 정보 표시 (회전 버튼 제거) */}
                {file.rotation_info && file.rotation_info.has_exif && file.rotation_info.needs_rotation && (
                  <span className="rotation-warning" title={`EXIF 방향: ${file.rotation_info.orientation}, 회전 필요: ${file.rotation_info.rotation_angle}도`}>
                    🔄 {file.rotation_info.rotation_angle}°
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 레이어 그룹 컴포넌트
interface LayerGroupProps {
  title: string;
  layers: Layer[];
  onToggleVisibility: (layerId: string) => void;
  onOpacityChange: (layerId: string, opacity: number) => void;
  onRemoveLayer?: (layerId: string) => void;
  onToggleFileVisibility?: (layerId: string, fileIndex: number, visible: boolean) => void;

  onTifFileSelect?: (layerId: string, fileIndex: number) => void;
  onTifFileDoubleClick?: (layerId: string, fileIndex: number) => void;
  onLayerDoubleClick?: (layerId: string) => void; // 레이어명 더블클릭 핸들러 추가
  onEgisLayerClick?: () => void; // EGIS 레이어 클릭 핸들러 추가
  onOpenLayerSettings?: (layerId: string, layerName: string, files: any[]) => void; // 레이어 설정 모달 열기 핸들러 추가
}

const LayerGroup: React.FC<LayerGroupProps> = ({
  title,
  layers,
  onToggleVisibility,
  onOpacityChange,
  onRemoveLayer,
  onToggleFileVisibility,
  onTifFileSelect,
  onTifFileDoubleClick,
  onLayerDoubleClick,
  onEgisLayerClick,
  onOpenLayerSettings
}) => {
  const { t } = useTranslation(); // 다국어 지원을 위한 훅 추가
  
  // localStorage에서 확장 상태를 가져오거나 기본값 false 사용 (초기 접속 시 접힌 상태)
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem(`layerGroup_${title}_expanded`);
    return saved !== null ? JSON.parse(saved) : false;
  });

  // 확장 상태 변경 시 localStorage에 저장
  const handleToggleExpanded = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    localStorage.setItem(`layerGroup_${title}_expanded`, JSON.stringify(newExpanded));
  };

  if (layers.length === 0) return null;

  return (
    <div className="qgis-layer-group">
      <div className="group-header" onClick={handleToggleExpanded}>
        <span className="expand-icon">{expanded ? '−' : '+'}</span>
        <span className="group-title">
          {title}
        </span>
        <span className="layer-count">({layers.length})</span>
      </div>
      
      {expanded && (
        <SortableContext
          items={layers.map(layer => layer.id)}
          strategy={verticalListSortingStrategy}
        >
        <div className="group-layers">
            {layers.map((layer) => (
            <LayerItem
              key={layer.id}
              layer={layer}
              onToggleVisibility={onToggleVisibility}
              onOpacityChange={onOpacityChange}
              onRemoveLayer={onRemoveLayer}
              onToggleFileVisibility={onToggleFileVisibility}

              onTifFileSelect={onTifFileSelect}
              onTifFileDoubleClick={onTifFileDoubleClick}
              onLayerDoubleClick={onLayerDoubleClick}
              onEgisLayerClick={onEgisLayerClick}
              onOpenLayerSettings={onOpenLayerSettings}
              isDraggable={layer.type !== LayerType.EGIS} // EGIS 레이어는 드래그 불가
            />
          ))}
        </div>
        </SortableContext>
      )}
    </div>
  );
};

// 메인 레이어 패널 컴포넌트
interface QGISLayerPanelProps {
  layerGroups: LayerGroup[];
  onLayersChange: (layers: Layer[]) => void;
  onToggleFileVisibility: (layerId: string, fileIndex: number, visible: boolean) => void;

  onTifFileSelect: (layerId: string, fileIndex: number) => void;
  onTifFileDoubleClick?: (layerId: string, fileIndex: number) => void;
  onLayerDoubleClick?: (layerId: string) => void; // 레이어명 더블클릭 핸들러 추가
  onEgisLayerClick: () => void; // EGIS 레이어 클릭 핸들러 추가
  onRefreshLayers: () => void; // 레이어 새로고침 핸들러 추가
  isCollapsed: boolean;
  onToggleCollapsed: () => void;

}

const QGISLayerPanel: React.FC<QGISLayerPanelProps> = ({ 
  layerGroups, 
  onLayersChange, 
  onToggleFileVisibility,
  onTifFileSelect,
  onTifFileDoubleClick,
  onLayerDoubleClick,
  onEgisLayerClick,
  onRefreshLayers,
  isCollapsed,
  onToggleCollapsed
}) => {
  const { t } = useTranslation();
  // 내부 축소/확장 상태
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const isCollapsedState = isCollapsed !== undefined ? isCollapsed : internalIsCollapsed;
  const handleToggleCollapsed = onToggleCollapsed || (() => setInternalIsCollapsed(!internalIsCollapsed));

  // 레이어 설정 모달 상태
  const [layerSettingsModal, setLayerSettingsModal] = useState<{
    isOpen: boolean;
    layerId: string;
    layerName: string;
    files: any[];
  }>({
    isOpen: false,
    layerId: '',
    layerName: '',
    files: []
  });

  // 드래그 앤 드롭 센서 설정 (Hook 규칙 준수를 위해 최상단에 배치)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 레이어 표시/숨김 토글 - 그룹별로 올바르게 처리
  const handleToggleVisibility = useCallback((layerId: string) => {
    console.log('=== 레이어 토글 시작 ===');
    console.log('토글 요청된 layerId:', layerId);
    
    // 해당 레이어 찾기
    let targetLayer: Layer | null = null;
    let targetGroupId: string | null = null;
    
    for (const group of layerGroups) {
      const layer = group.layers.find(l => l.id === layerId);
      if (layer) {
        targetLayer = layer;
        targetGroupId = group.id;
        break;
      }
    }
    
    if (!targetLayer || !targetGroupId) {
      console.error('레이어를 찾을 수 없음:', layerId);
      return;
    }
    
    const newVisible = !targetLayer.visible;
    console.log(`${targetLayer.name} (${targetLayer.type}) visible 상태 변경: ${targetLayer.visible} -> ${newVisible}`);
    
    // 그룹별로 레이어 업데이트
    const updatedGroups = layerGroups.map(group => {
      if (group.id === targetGroupId) {
        return {
          ...group,
          layers: group.layers.map(layer => 
            layer.id === layerId 
              ? { ...layer, visible: newVisible }
              : layer
          )
        };
      }
      return group;
    });
    
    // 평면화된 레이어 배열 생성 (onLayersChange 호출용)
    const allLayers = updatedGroups.flatMap(group => group.layers);
    
    console.log('토글 후 레이어 상태:', allLayers.map(l => ({ id: l.id, name: l.name, type: l.type, visible: l.visible })));
    onLayersChange(allLayers);
    console.log('=== 레이어 토글 완료 ===');
  }, [layerGroups, onLayersChange]);

  // 투명도 변경 - 그룹별로 처리
  const handleOpacityChange = useCallback((layerId: string, opacity: number) => {
    // 해당 레이어 찾기
    let targetGroupId: string | null = null;
    
    for (const group of layerGroups) {
      const layer = group.layers.find(l => l.id === layerId);
      if (layer) {
        targetGroupId = group.id;
        break;
      }
    }
    
    if (!targetGroupId) {
      console.error('레이어를 찾을 수 없음:', layerId);
      return;
    }
    
    const updatedGroups = layerGroups.map(group => {
      if (group.id === targetGroupId) {
        return {
          ...group,
          layers: group.layers.map(layer =>
            layer.id === layerId 
              ? { ...layer, opacity }
              : layer
          )
        };
      }
      return group;
    });
    
    const allLayers = updatedGroups.flatMap(group => group.layers);
    onLayersChange(allLayers);
  }, [layerGroups, onLayersChange]);



  // 레이어 제거 - 그룹별로 처리
  const handleRemoveLayer = useCallback((layerId: string) => {
    // 해당 레이어 찾기
    let targetGroupId: string | null = null;
    
    for (const group of layerGroups) {
      const layer = group.layers.find(l => l.id === layerId);
      if (layer) {
        targetGroupId = group.id;
        break;
      }
    }
    
    if (!targetGroupId) {
      console.error('레이어를 찾을 수 없음:', layerId);
      return;
    }
    
    const updatedGroups = layerGroups.map(group => {
      if (group.id === targetGroupId) {
        return {
          ...group,
          layers: group.layers.filter(layer => layer.id !== layerId)
        };
      }
      return group;
    });
    
    const allLayers = updatedGroups.flatMap(group => group.layers);
    onLayersChange(allLayers);
  }, [layerGroups, onLayersChange]);

  // 레이어 설정 모달 열기
  const handleOpenLayerSettings = useCallback((layerId: string, layerName: string, files: any[]) => {
    setLayerSettingsModal({
      isOpen: true,
      layerId,
      layerName,
      files
    });
  }, []);

  // 레이어 설정 모달 닫기
  const handleCloseLayerSettings = useCallback(() => {
    setLayerSettingsModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // 레이어 설정 저장
  const handleSaveLayerSettings = useCallback((settings: any) => {
    console.log('레이어 설정 저장됨:', settings);
    
    // 회전 설정이 변경된 경우 이미지 새로고침
    if (settings.default_rotation !== undefined || settings.files || settings.auto_rotate !== undefined) {
      console.log('회전 설정이 변경되어 이미지를 새로고침합니다.');
      
      // 레이어 새로고침 (부모 컴포넌트에서 전달받은 핸들러 사용)
      if (onRefreshLayers) {
        onRefreshLayers();
      }
      
      // 또는 강제로 이미지 URL에 타임스탬프 추가하여 캐시 무효화
      setTimeout(() => {
        // 이미지 요소들을 찾아서 src를 강제로 새로고침
        const images = document.querySelectorAll('img[src*="/api/upload/overlay/"]');
        images.forEach((img) => {
          const imgElement = img as HTMLImageElement;
          const originalSrc = imgElement.src;
          const separator = originalSrc.includes('?') ? '&' : '?';
          imgElement.src = `${originalSrc}${separator}_t=${Date.now()}`;
        });
      }, 100);
    }
  }, [onRefreshLayers]);

  // 파일 가시성 토글 (외부에서 전달받은 핸들러 사용)
  const handleToggleFileVisibility = useCallback((layerId: string, fileIndex: number, visible: boolean) => {
    if (onToggleFileVisibility) {
      onToggleFileVisibility(layerId, fileIndex, visible);
    } else {
      // 내부 처리 (기존 로직)
    const updatedLayers = layerGroups.flatMap(group => 
        group.layers.map(layer => {
            if (layer.id === layerId && layer.files) {
              const updatedFiles = [...layer.files];
              if (updatedFiles[fileIndex]) {
                updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], visible };
              }
              return { ...layer, files: updatedFiles };
          }
          return layer;
      })
    );
    onLayersChange(updatedLayers);
    }
  }, [layerGroups, onLayersChange, onToggleFileVisibility]);

  // 드래그 앤 드롭으로 레이어 순서 변경
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    console.log('=== 드래그 앤 드롭 이벤트 시작 ===');
    console.log('드래그 이벤트:', event);
    
    const { active, over } = event;
    console.log('active:', active);
    console.log('over:', over);

    if (active.id !== over?.id) {
      console.log('드래그 위치가 변경됨');
      
      // 사용자 정의 레이어만 순서 변경 가능
      const activeLayer = layerGroups.flatMap(group => group.layers).find(layer => layer.id === active.id);
      const overLayer = layerGroups.flatMap(group => group.layers).find(layer => layer.id === over?.id);
      
      console.log('activeLayer:', activeLayer);
      console.log('overLayer:', overLayer);
      
      if (activeLayer && overLayer && 
          activeLayer.type !== LayerType.EGIS && 
          overLayer.type !== LayerType.EGIS) {
        
        console.log('사용자 정의 레이어 간 순서 변경 가능');
        
        // 전체 레이어 배열에서 순서 변경
      const oldIndex = layerGroups.flatMap(group => group.layers).findIndex(layer => layer.id === active.id);
        const newIndex = layerGroups.flatMap(group => group.layers).findIndex(layer => layer.id === over?.id);
      
        console.log('oldIndex:', oldIndex, 'newIndex:', newIndex);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedLayers = arrayMove(layerGroups.flatMap(group => group.layers), oldIndex, newIndex);
          console.log(`레이어 순서 변경: ${active.id} (${oldIndex}) -> ${over?.id} (${newIndex})`);
          console.log('변경된 레이어 순서:', reorderedLayers.map(l => ({ id: l.id, name: l.name, type: l.type })));
          onLayersChange(reorderedLayers);
          console.log('onLayersChange 호출 완료');
        } else {
          console.log('인덱스를 찾을 수 없음');
        }
      } else {
        console.log('EGIS 레이어는 순서 변경할 수 없습니다.');
      }
    } else {
      console.log('드래그 위치가 변경되지 않음');
    }
    console.log('=== 드래그 앤 드롭 이벤트 완료 ===');
  }, [layerGroups, onLayersChange]);

  // 레이어를 그룹으로 분류 (useMemo로 최적화)
  const { egisLayers, userLayers } = useMemo(() => {
    const egis = layerGroups.flatMap(group => group.layers.filter(layer => layer.type === LayerType.EGIS));
    const user = layerGroups.flatMap(group => group.layers.filter(layer => layer.type !== LayerType.EGIS));
    return { egisLayers: egis, userLayers: user };
  }, [layerGroups]);

  return (
    <div className={`qgis-layer-panel ${isCollapsedState ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <h3>{t('layers.title')}</h3>
        <div className="header-buttons">
          {onRefreshLayers && (
            <button 
              className="refresh-btn"
              onClick={onRefreshLayers}
              title={t('layers.refresh') as string}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </button>
          )}
          <button 
            className="collapse-btn"
            onClick={handleToggleCollapsed}
            title={t('common.toggleLeftSidebar') as string}
          >
            {isCollapsedState ? '▶' : '◀'}
          </button>
        </div>
      </div>

      {!isCollapsedState && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="panel-content">
            {/* EGIS 레이어 그룹 */}
            <LayerGroup
              title={t('landCover.egis2025') as string}
              layers={egisLayers}
              onToggleVisibility={handleToggleVisibility}
              onOpacityChange={handleOpacityChange}
              onLayerDoubleClick={onLayerDoubleClick}
              onEgisLayerClick={onEgisLayerClick}
            />
            

            
            {/* 사용자 정의 레이어 그룹 */}
            <LayerGroup
              title={t('landCover.userUpload') as string}
              layers={userLayers}
              onToggleVisibility={handleToggleVisibility}
              onOpacityChange={handleOpacityChange}
              onRemoveLayer={handleRemoveLayer}
              onToggleFileVisibility={handleToggleFileVisibility}

              onTifFileSelect={onTifFileSelect}
              onTifFileDoubleClick={onTifFileDoubleClick}
              onLayerDoubleClick={onLayerDoubleClick}
              onOpenLayerSettings={handleOpenLayerSettings}
            />
          
          {/* 레이어 추가 버튼 */}
          {/* 레이어 추가 버튼은 개별 레이어 그룹에서 처리하므로 여기서는 제거 */}
        </div>
        </DndContext>
      )}

      {/* 레이어 설정 모달 */}
      <LayerSettingsModal
        isOpen={layerSettingsModal.isOpen}
        onClose={handleCloseLayerSettings}
        layerId={layerSettingsModal.layerId}
        layerName={layerSettingsModal.layerName}
        files={layerSettingsModal.files}
        onSaveSettings={handleSaveLayerSettings}
      />
    </div>
  );
};

export default QGISLayerPanel; 